// Full employee field catalog for the manager page's View Employee modal —
// ported verbatim from the admin Employee Management page's
// components/modals/EditEmployeeModal.jsx (`FIELD_SECTIONS`) so both pages
// show the exact same fields, grouped the exact same way.
//
// Previously this page's ViewEmployeeModal imported FIELD_SECTIONS directly
// from "../../../(admin)employeemanagement/components/modals/EditEmployeeModal",
// reaching into a sibling page's internals across the folder boundary. That
// meant every edit to the admin edit form's field catalog could silently
// change what the manager page's read-only view modal renders, and the
// manager bundle pulled in the (much larger) admin edit-form component just
// to read one exported constant. This file is the manager page's own copy —
// data only, no coupling.
import {
  User,
  Building,
  Clock,
  FileText,
  Shield,
  GraduationCap,
  CheckCircle,
  Home,
  Award,
  Heart,
  Flag,
} from "lucide-react";

export const FIELD_SECTIONS = [
  {
    key: "basic",
    title: { en: "Basic Information", ar: "المعلومات الأساسية" },
    icon: User,
    fields: [
      { name: "sr", label: { en: "Full Name (English)", ar: "الاسم الكامل (إنجليزي)" }, type: "text" },
      { name: "arabicName", label: { en: "Full Name (Arabic)", ar: "الاسم الكامل (عربي)" }, type: "text" },
      { name: "email", label: { en: "Email", ar: "البريد الإلكتروني" }, type: "email" },
      { name: "phoneNumber", label: { en: "Phone Number", ar: "رقم الهاتف" }, type: "tel" },
      { name: "emergencyPhoneNumber", label: { en: "Emergency Phone", ar: "هاتف الطوارئ" }, type: "tel" },
      { name: "nationalId", label: { en: "National ID", ar: "الرقم القومي" }, type: "text" },
      { name: "idRegion", label: { en: "ID Region", ar: "منطقة إصدار البطاقة" }, type: "text" },
      { name: "dob", label: { en: "Date of Birth", ar: "تاريخ الميلاد" }, type: "date" },
      { name: "age", label: { en: "Age", ar: "العمر" }, type: "number" },
      { name: "gender", label: { en: "Gender", ar: "النوع" }, type: "text" },
      { name: "maritalStatus", label: { en: "Marital Status", ar: "الحالة الاجتماعية" }, type: "text" },
      { name: "nationality", label: { en: "Nationality", ar: "الجنسية" }, type: "text" },
      { name: "religion", label: { en: "Religion", ar: "الديانة" }, type: "text" },
      { name: "militaryStatus", label: { en: "Military Status", ar: "الموقف من التجنيد" }, type: "text" },
    ],
  },
  {
    key: "address",
    title: { en: "Address", ar: "العنوان" },
    icon: Home,
    fields: [
      { name: "address", label: { en: "Address (English)", ar: "العنوان (إنجليزي)" }, type: "text" },
      { name: "arabicAddress", label: { en: "Address (Arabic)", ar: "العنوان (عربي)" }, type: "text" },
      { name: "city", label: { en: "City", ar: "المدينة" }, type: "text" },
      { name: "country", label: { en: "Country", ar: "الدولة" }, type: "text" },
    ],
  },
  {
    key: "work",
    title: { en: "Work Information", ar: "معلومات العمل" },
    icon: Building,
    fields: [
      { name: "workingCompany", label: { en: "Working Company", ar: "الشركة العاملة" }, type: "text" },
      { name: "departmentId", label: { en: "Department", ar: "القسم" }, type: "department" },
      { name: "position", label: { en: "Position", ar: "المسمى الوظيفي" }, type: "text" },
      { name: "location", label: { en: "Location", ar: "الموقع" }, type: "text" },
      { name: "clientName", label: { en: "Client Name", ar: "اسم العميل" }, type: "text" },
      { name: "clientCode", label: { en: "Client Code", ar: "كود العميل" }, type: "text" },
      { name: "contractCompany", label: { en: "Contract Company", ar: "شركة التعاقد" }, type: "text" },
      { name: "employeeState", label: { en: "Status", ar: "الحالة" }, type: "text" },
      { name: "laborMode", label: { en: "Labor Mode", ar: "نمط العمل" }, type: "text" },
      { name: "hiringDate", label: { en: "Hiring Date", ar: "تاريخ التعيين" }, type: "date" },
      { name: "resignedDate", label: { en: "Resigned Date", ar: "تاريخ الاستقالة" }, type: "date" },
      { name: "resignReasons", label: { en: "Resign Reasons", ar: "أسباب الاستقالة" }, type: "text" },
      { name: "workingAgeYears", label: { en: "Working Age (Years)", ar: "سنوات الخبرة" }, type: "number" },
      { name: "workingAgeMonths", label: { en: "Working Age (Months)", ar: "شهور الخبرة" }, type: "number" },
    ],
  },
  {
    key: "management",
    title: { en: "Management", ar: "الإدارة" },
    icon: Award,
    fields: [
      { name: "firstLineManagerName", label: { en: "1st Line Manager", ar: "المدير المباشر" }, type: "text" },
      { name: "secondLineManagerName", label: { en: "2nd Line Manager", ar: "المدير الثاني" }, type: "text" },
    ],
  },
  // Compensation section removed — managers do not have access to salary data.
  {
    key: "contract",
    title: { en: "Contract & Regularization", ar: "التعاقد والتثبيت" },
    icon: FileText,
    fields: [
      { name: "contractDate", label: { en: "Contract Date", ar: "تاريخ العقد" }, type: "date" },
      { name: "termOfContract", label: { en: "Term of Contract (months)", ar: "مدة العقد (شهور)" }, type: "number" },
      { name: "contractExpiration", label: { en: "Contract Expiration", ar: "انتهاء العقد" }, type: "date" },
      { name: "lastContractDate", label: { en: "Last Contract Date", ar: "تاريخ آخر عقد" }, type: "date" },
      { name: "lastContractTerm", label: { en: "Last Contract Term", ar: "مدة آخر عقد" }, type: "text" },
      { name: "lastContractExpiration", label: { en: "Last Contract Expiration", ar: "انتهاء آخر عقد" }, type: "date" },
      { name: "toBeRegular", label: { en: "To Be Regular", ar: "قابل للتثبيت" }, type: "boolean" },
      { name: "beRegularDate", label: { en: "Be-Regular Date", ar: "تاريخ التثبيت" }, type: "date" },
      { name: "periodForBeingRegular", label: { en: "Period for Being Regular", ar: "فترة التثبيت" }, type: "text" },
    ],
  },
  {
    key: "insurance",
    title: { en: "Insurance & Medical", ar: "التأمين والرعاية الطبية" },
    icon: Heart,
    fields: [
      { name: "insuranceStatus", label: { en: "Insurance Status", ar: "حالة التأمين" }, type: "text" },
      { name: "insuranceNumber", label: { en: "Insurance Number", ar: "الرقم التأميني" }, type: "text" },
      { name: "insuredBeginningDate", label: { en: "Insurance Start Date", ar: "تاريخ بداية التأمين" }, type: "date" },
      { name: "monthsForInsurance", label: { en: "Months for Insurance", ar: "شهور التأمين" }, type: "number" },
      { name: "medicalInsuranceStage", label: { en: "Medical Insurance Stage", ar: "فئة التأمين الطبي" }, type: "text" },
      { name: "medicalNumber", label: { en: "Medical Number", ar: "الرقم الطبي" }, type: "text" },
      { name: "didMedicalCheck", label: { en: "Did Medical Check", ar: "أجرى الفحص الطبي" }, type: "boolean" },
      { name: "delivered", label: { en: "Delivered", ar: "تم التسليم" }, type: "boolean" },
    ],
  },
  {
    key: "education",
    title: { en: "Education", ar: "التعليم" },
    icon: Flag,
    fields: [
      { name: "graduateInstitution", label: { en: "Graduate Institution", ar: "جهة التخرج" }, type: "text" },
      { name: "major", label: { en: "Major", ar: "التخصص" }, type: "text" },
      { name: "graduationYear", label: { en: "Graduation Year", ar: "سنة التخرج" }, type: "number" },
    ],
  },
  {
    key: "licenses",
    title: { en: "Licenses & Health Certificates", ar: "الرخص والشهادات الصحية" },
    icon: CheckCircle,
    fields: [
      { name: "drivingLicenseDegree", label: { en: "Driving License Degree", ar: "درجة رخصة القيادة" }, type: "text" },
      { name: "drivingLicenseExpiry", label: { en: "Driving License Expiry", ar: "انتهاء رخصة القيادة" }, type: "date" },
      { name: "drivingLicenseStillValid", label: { en: "Driving License Valid", ar: "رخصة القيادة سارية" }, type: "boolean" },
      { name: "healthCertificateOB", label: { en: "Health Certificate (Office Boys)", ar: "شهادة صحية (أوفيس بوي)" }, type: "boolean" },
      { name: "healthCertExpiryDate", label: { en: "Health Certificate Expiry", ar: "انتهاء الشهادة الصحية" }, type: "date" },
      { name: "healthCertStillValid", label: { en: "Health Certificate Valid", ar: "الشهادة الصحية سارية" }, type: "boolean" },
      { name: "uniformSize", label: { en: "Uniform Size", ar: "مقاس الزي" }, type: "text" },
    ],
  },
  {
    key: "system",
    title: { en: "System Information", ar: "معلومات النظام" },
    icon: Clock,
    fields: [
      { name: "infinityCode", label: { en: "Infinity Code", ar: "كود إنفينيتي" }, type: "text" },
      { name: "id", label: { en: "Database ID", ar: "معرف قاعدة البيانات" }, type: "text" },
      { name: "created_at", label: { en: "Created At", ar: "تاريخ الإنشاء" }, type: "date" },
      { name: "updated_at", label: { en: "Last Updated", ar: "آخر تحديث" }, type: "date" },
    ],
  },
];

export const SECTION_ICONS = FIELD_SECTIONS.reduce((acc, section) => {
  acc[section.key] = section.icon;
  return acc;
}, {});

// Fields rendered as currency in the view modal.
// Compensation/salary fields are intentionally excluded — managers do not see pay data.
export const MONEY_FIELDS = new Set([]);
